import { z } from "zod/v3";
import {
  validateSessionTimes,
  validateOnsiteFields,
  validateFacility,
  validateRegistrationOptions,
  validateProduct,
  validatePATSessionDate,
  validatePATRegistrationCloseDate
} from "./utils";

const DateSchema = z.object({
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required")
});

export const SessionSchema = z
  .object({
    id: z.string().optional(),
    date: DateSchema,
    startTime: z.string().min(1, "Start time is required"),
    endTime: z.string().min(1, "End time is required"),
    maxAttendance: z.string().optional()
  })
  .refine(validateSessionTimes, {
    message: "End time must be after start time",
    path: ["endTime"]
  });

export const ProductSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    variantId: z.string(),
    variantName: z.string(),
    variantPrice: z.number(),
    collection: z.string()
  })
  .refine(validateProduct, {
    message: "Variant and collection are required",
    path: ["product"]
  })
  .superRefine((data, ctx) => {
    if (data.id && data.variantPrice <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Price must be more than $0",
        path: ["variantPrice"]
      });
    }
  });

export const EventFeeSchema = z
  .object({
    id: z.string().optional(),
    kind: z.enum(["catalog", "external"]),
    paymentCollection: z.enum(["registration", "on_site", "external_system"]),
    productVariantId: z.string(),
    productVariantName: z.string(),
    amountCents: z.number(),
    displayName: z.string()
  })
  .superRefine((data, ctx) => {
    if (data.kind === "catalog") {
      if (!data.productVariantId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Product variant is required",
          path: ["productVariantId"]
        });
      }

      if (data.paymentCollection !== "registration") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "PGA-collected fees must be collected during registration",
          path: ["paymentCollection"]
        });
      }
    }

    if (data.kind === "external") {
      if (!data.displayName.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Override label is required",
          path: ["displayName"]
        });
      }

      if (data.amountCents <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Price must be more than $0",
          path: ["amountCents"]
        });
      }

      if (data.paymentCollection !== "external_system") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "External fees must use the external system collection type",
          path: ["paymentCollection"]
        });
      }
    }
  });

export const createEventSchema = (isEditMode?: boolean, originalRegistrationCloseDate?: any) =>
  z
    .object({
      eventName: z.string().min(1, "Event Name is required"),
      eventType: z.object({
        id: z.string().min(1, "Event Type is required"),
        name: z.string().min(1, "Event Type is required"),
        source: z.enum(["activityType", "legacyEventType"]).optional()
      }),
      draft: z.boolean(),
      section: z.string().min(1, "Section is required"),
      sectionId: z.string(),
      legacyId: z.string(),
      universityIds: z.array(z.string()).default([]),
      orderTemplateId: z.string().optional(),
      locationType: z.enum(["onsite", "virtual"]),
      facility: z.string().optional(),
      facilityId: z.string().optional(),
      facilityName: z.string().optional(),
      address1: z.string().optional(),
      address2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zipcode: z.string().optional(),
      timezone: z.string(),
      location: z.string().optional(),
      description: z.string().optional(),
      bannerImageUrl: z.string(),
      recruitmentLeadFormUrl: z.string().optional(),
      sessions: z.array(SessionSchema).min(1, "At least one session is required"),
      registrationType: z.enum(["individual", "all"]),
      globalMaxAttendance: z.string().optional(),
      registrationOpenDate: DateSchema,
      registrationCloseDate: DateSchema,
      products: z.array(ProductSchema).optional(),
      eventFees: z.array(EventFeeSchema).optional(),
      waitlistType: z.enum(["disabled", "notification"]),
      customFields: z.array(z.string()).optional(),
      termsContent: z.string().optional(),
      collectsAttendance: z.boolean().optional(),
      patEventAttributes: z.object({
        yardageMale: z.string().optional(),
        courseRatingMale: z.string().optional(),
        yardageMaleFiftyPlus: z.string().optional(),
        yardageFemale: z.string().optional(),
        courseRatingFemale: z.string().optional(),
        yardageFemaleFiftyPlus: z.string().optional(),
        hostProfessional: z.object({
          id: z.string().optional(),
          profilePhoto: z.string().optional(),
          name: z.string().optional()
        }),
        examiner: z.object({
          id: z.string().optional(),
          profilePhoto: z.string().optional(),
          name: z.string().optional()
        })
      }),
      recruitmentEventAttributes: z.object({
        recruitmentEventType: z.string().optional(),
        purpose: z.string().optional(),
        strategicFunding: z.string().optional(),
        pgaRepresentation: z.array(z.string()).optional(),
        supportRequested: z.array(z.string()).optional(),
        estimatedExpenses: z.number().optional(),
        sponsorshipAsk: z.number().optional(),
        estimatedParents: z.string().optional(),
        estimatedCoaches: z.string().optional(),
        estimatedOtherAttendees: z.string().optional(),
        totalAttendees: z.string().optional(),
        additionalComments: z.string().optional(),
        eventRecap: z.string().optional(),
        overallFeedback: z.string().optional(),
        eventRating: z.string().optional(),
        commentsAndQuotes: z.string().optional(),
        photoUploads: z.array(z.string()).optional(),
        isFinalized: z.boolean().optional()
      })
    })
    .superRefine((data, ctx) => {
      if (data.locationType === "onsite") {
        validateOnsiteFields(data, ctx);
      }

      if (data.eventType.name === "PAT") {
        validateFacility(data, ctx);
        validatePATSessionDate(data.sessions, ctx, isEditMode, originalRegistrationCloseDate);
        validatePATRegistrationCloseDate(data.sessions, data.registrationCloseDate.startDate, ctx);
      }

      if (data.eventType.name.toLowerCase().includes("university") && data.universityIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Managing University is required",
          path: ["universityIds"]
        });
      }

      if (data.eventType.name === "Recruitment") {
        // Validate required fields for Recruitment events
        if (!data.recruitmentEventAttributes?.recruitmentEventType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Recruitment Event Type is required",
            path: ["recruitmentEventAttributes", "recruitmentEventType"]
          });
        }
        if (!data.recruitmentEventAttributes?.purpose) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Purpose is required",
            path: ["recruitmentEventAttributes", "purpose"]
          });
        }
        if (!data.recruitmentEventAttributes?.strategicFunding) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Strategic Funding is required",
            path: ["recruitmentEventAttributes", "strategicFunding"]
          });
        }
        if (
          !data.recruitmentEventAttributes?.pgaRepresentation ||
          data.recruitmentEventAttributes.pgaRepresentation.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "PGA Representation is required",
            path: ["recruitmentEventAttributes", "pgaRepresentation"]
          });
        }
      }

      validateRegistrationOptions(data, ctx);
    });

export type FormValues = z.infer<ReturnType<typeof createEventSchema>>;
